import React, { useState } from 'react';
import { Card, Tabs, Table, Button, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';

async function fetchReport(path: string) {
  const res = await api.get(`/manufacturing/reports/outsourced/${path}`);
  return (res as { data?: unknown })?.data ?? res;
}

const OutsourcedFabricationReportsPage: React.FC = () => {
  const [tab, setTab] = useState('operation-status');

  const { data: operationStatus = [], refetch: r1, isFetching: f1 } = useQuery({
    queryKey: ['outsourced-report', 'operation-status'],
    queryFn: () => fetchReport('operation-status'),
    enabled: tab === 'operation-status',
  });

  const { data: vendorCompletion = [], refetch: r2, isFetching: f2 } = useQuery({
    queryKey: ['outsourced-report', 'vendor-completion'],
    queryFn: () => fetchReport('vendor-completion'),
    enabled: tab === 'vendor-completion',
  });

  const { data: vendorQuality = [], refetch: r3, isFetching: f3 } = useQuery({
    queryKey: ['outsourced-report', 'vendor-quality'],
    queryFn: () => fetchReport('vendor-quality'),
    enabled: tab === 'vendor-quality',
  });

  const { data: inspectionDelay = [], refetch: r4, isFetching: f4 } = useQuery({
    queryKey: ['outsourced-report', 'inspection-delay'],
    queryFn: () => fetchReport('inspection-delay'),
    enabled: tab === 'inspection-delay',
  });

  const { data: allocationHistory = [], refetch: r5, isFetching: f5 } = useQuery({
    queryKey: ['outsourced-report', 'allocation-history'],
    queryFn: () => fetchReport('allocation-history'),
    enabled: tab === 'allocation-history',
  });

  const items = [
    {
      key: 'operation-status',
      label: 'Operation status',
      children: (
        <Table
          loading={f1}
          dataSource={Array.isArray(operationStatus) ? operationStatus : []}
          rowKey={(r: Record<string, unknown>) =>
            `${r.work_order_id}-${r.production_unit_number}-${r.outsourced_trace_status}`
          }
          columns={[
            { title: 'WO', dataIndex: 'work_order_number' },
            { title: 'Line', dataIndex: 'line_number' },
            { title: 'Operation', dataIndex: 'operation_name' },
            { title: 'Unit', dataIndex: 'production_unit_number' },
            { title: 'Trace', dataIndex: 'outsourced_trace_status' },
            { title: 'Stage', dataIndex: 'stage_status' },
            { title: 'PR', dataIndex: 'pr_number' },
          ]}
          pagination={{ pageSize: 50 }}
        />
      ),
    },
    {
      key: 'vendor-completion',
      label: 'Vendor completion',
      children: (
        <Table
          loading={f2}
          dataSource={Array.isArray(vendorCompletion) ? vendorCompletion : []}
          rowKey={(r: Record<string, unknown>) => String(r.purchase_order_id)}
          columns={[
            { title: 'Supplier', dataIndex: 'supplier_name' },
            { title: 'PO', dataIndex: 'po_number' },
            { title: 'Ordered', dataIndex: 'ordered_qty' },
            { title: 'Received', dataIndex: 'received_qty' },
          ]}
        />
      ),
    },
    {
      key: 'vendor-quality',
      label: 'Vendor quality',
      children: (
        <Table
          loading={f3}
          dataSource={Array.isArray(vendorQuality) ? vendorQuality : []}
          rowKey={(r: Record<string, unknown>, i) => `${r.supplier_id}-${r.result}-${i}`}
          columns={[
            { title: 'Supplier', dataIndex: 'supplier_name' },
            { title: 'Result', dataIndex: 'result' },
            { title: 'Disposition', dataIndex: 'disposition' },
            { title: 'Count', dataIndex: 'inspection_count' },
          ]}
        />
      ),
    },
    {
      key: 'inspection-delay',
      label: 'Inspection delay',
      children: (
        <Table
          loading={f4}
          dataSource={Array.isArray(inspectionDelay) ? inspectionDelay : []}
          rowKey={(r: Record<string, unknown>) => String(r.work_order_stage_id)}
          columns={[
            { title: 'WO', dataIndex: 'work_order_number' },
            { title: 'Unit', dataIndex: 'production_unit_number' },
            { title: 'Status', dataIndex: 'outsourced_trace_status' },
            { title: 'Days', dataIndex: 'days_in_status' },
          ]}
        />
      ),
    },
    {
      key: 'allocation-history',
      label: 'Allocation history',
      children: (
        <Table
          loading={f5}
          dataSource={Array.isArray(allocationHistory) ? allocationHistory : []}
          rowKey={(r: Record<string, unknown>) => String(r.id)}
          columns={[
            { title: 'WO', dataIndex: 'work_order_number' },
            { title: 'GRN', dataIndex: 'grn_number' },
            { title: 'Unit', dataIndex: 'production_unit_number' },
            { title: 'Allocated', dataIndex: 'allocated_at' },
          ]}
        />
      ),
    },
  ];

  const refetchMap: Record<string, () => void> = {
    'operation-status': r1,
    'vendor-completion': r2,
    'vendor-quality': r3,
    'inspection-delay': r4,
    'allocation-history': r5,
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Manufacturing"
          documentNumber="Outsourced fabrication"
          subtitle="Status, vendor completion, quality, inspection delay, and GRN allocation history"
        />
      }
    >
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button onClick={() => refetchMap[tab]?.()}>Refresh</Button>
        </Space>
        <Tabs activeKey={tab} onChange={setTab} items={items} />
      </Card>
    </WorkspaceLayout>
  );
};

export default OutsourcedFabricationReportsPage;
